import {useRef} from 'react'
import useControlledState from '@/hooks/useControlledState.mjs'

const useRowSelection = (options, rows, rowKey, iterRows) => {
    const refInitialSelected = useRef(false)
    const flatRows = [...iterRows(rows)]

    const rowMap = new Map(
        Array.from(flatRows).map(
            e => [e[rowKey], e]
        )
    )

    const [selectedRowKeys, setSelectedRowKeys] = useControlledState(
        options?.defaultSelectedRowKeys ?? [],
        options?.selectedRowKeys,

        keys => {
            const rows = keys.map(k => rowMap.get(k))
            options.onChange?.(keys, rows)
        }
    )

    if (! options) {
        return [null, setSelectedRowKeys]
    }

    const {
        defaultSelect,
        defaultSelectAll,
        defaultSelectFirst,
        disabled,
        ...antdOptions
    } = options

    if (
        ! refInitialSelected.current &&
        0 < flatRows.length &&
        (defaultSelect || defaultSelectAll || defaultSelectFirst)
    ) {
        refInitialSelected.current = true

        const selectableRows = disabled ?
            flatRows.filter(e => ! disabled(e))
            :
            flatRows

        if (0 < selectableRows.length) {
            const keys = (() => {
                if (defaultSelect) {
                    return defaultSelect(selectableRows)
                }
                else if (defaultSelectAll) {
                    return selectableRows.map(e => e[rowKey])
                }
                else if (defaultSelectFirst) {
                    return [selectableRows[0][rowKey]]
                }
            })()

            setSelectedRowKeys(keys)
        }
    }

    const cleanKeys = selectedRowKeys.filter(k => rowMap.has(k))

    const rowSelection = {
        columnWidth: 40,
        ...antdOptions,

        getCheckboxProps: row => {
            const props = antdOptions.getCheckboxProps?.(row)

            return {
                ...props,
                disabled: props?.disabled || disabled?.(row),
            }
        },

        selectedRowKeys: cleanKeys,
        onChange: selectedRowKeys => setSelectedRowKeys(selectedRowKeys)
    }

    return [rowSelection, setSelectedRowKeys]
}

export default useRowSelection
